import _ from "lodash";
import React, { useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import client from "lib/apolloClient/client";
import classes from "./StandardV2.scss";
import Header from "./components/Header";
import {
  SearchInput,
  FilterLabelButton,
  ChecklistDropdown,
} from "@toddle-edu/ds-web";
import {
  deleteStandard,
  changeStandardSearchString,
  updateFilters,
} from "Tooling/modules/ToolingModule";
import {
  getAllGlobalSubjectsQuery,
  getStandardFeedQuery,
  getStandardBenchmarkSetDownloadUrlQuery,
} from "Tooling/modules/ToolingQueries";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import I18nHOC from "UIComponents/I18nHOC";
import DialogueBox from "UIComponents/DialogueBox";
import Loading from "UIComponents/Loading";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import FeedListv2 from "AppComponents/FeedListv2";
import ManageStandard from "./components/ManageStandard";
import { CURRICULUM_FLAVOURS } from "Tooling/modules/ToolingUtils";
import { useHandleScroll } from "Utils/CustomHooks";
import { goToBack } from "modules/NavigationModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getCurriculumAcronym, getSubjectsData } from "./modules/Utils";
import { downloadFromUrl } from "modules/Services";

const unassignedToAnySubjectOption = [
  {
    isDivider: true,
  },
  {
    value: "UNASSIGNED",
    label: "Unassigned to any subject",
  },
];

const columnList = () => [
  {
    label: "Standard name",
    id: "standardName",
    type: "TEXTCOLUMN",
    style: {
      maxWidth: "280px",
    },
    isSticky: true,
  },
  {
    label: "Code",
    id: "code",
    type: "TEXTCOLUMN",
  },
  {
    label: "Curriculum Name",
    id: "curriculumName",
    type: "TEXTCOLUMN",
    style: {
      maxWidth: "200px",
    },
  },
  {
    label: "Country",
    id: "country",
    type: "TEXTCOLUMN",
  },
  {
    label: "State",
    id: "state",
    type: "TEXTCOLUMN",
  },
  {
    label: "Curriculum",
    id: "curriculumTypes",
    type: "ARRAYCOLUMNWITHOVER",
  },
  {
    label: "Subjects",
    id: "globalSubjects",
    type: "ARRAYCOLUMNWITHOVER",
  },
  {
    label: "Count",
    id: "count",
    type: "INTCOLUMN",
    style: {
      maxWidth: "186px",
    },
    valueTooltipMaxWidth: "356px",
  },
  {
    label: "Actions",
    id: "action",
    type: "ACTIONS",
  },
];

const DIALOGS_INFO = {
  DELETE_STANDARD: {
    title: "Delete Standard",
    message: ({ title }) => {
      return (
        <div>
          <div className={classes.deleteStandardContainer}>
            {`Are you sure you want to delete the Standard? This action can't be undone.`}
          </div>
          <div>
            Standard title :
            <span className={classes.deleteStandardLabel}>{title}</span>
          </div>
        </div>
      );
    },
    confirmButtonText: "Delete",
    cancelButtonText: "Cancel",
  },
};

const DIALOGBOX_CONFIG = {
  editStandardInfo: {
    mode: "edit",
    title: "Edit Standard",
  },
  createStandard: {
    mode: "create",
    title: "Create new standard",
  },
};

const StandardV2 = ({
  filters,
  standardFilters,
  standards,
  subjectsData,
  deleteStandard,
  updateFilters,
  downloadFromUrl,
  changeStandardSearchString,
  goToBack,
  t,
  networkStatus,
  isLoading,
  isData,
  getStandardFeed,
  loadMoreFeed,
  hasNextPage,
  setToastMsg,
}) => {
  const [standardObj, setStandardObj] = useState({});
  const [showDeleteDialogBox, setShowDeleteDialogBox] = useState(false);
  const [isStateLoading, setIsStateLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    show: false,
    screen: null,
  });

  const containerRef = useRef();

  const getScrollRef = () => {
    return containerRef.current;
  };

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef,
  });
  const {
    subjectDropdownOptionsForFilter,
    subjectDropdownOptionsForModal,
  } = useMemo(() => {
    const subjectsOption = _.map(subjectsData, subject => {
      const curriculumAcroynm = getCurriculumAcronym({
        curriculumType: subject.curriculumType,
      });

      return {
        ...subject,
        label: `${subject.label} (${curriculumAcroynm})`,
      };
    });

    return {
      subjectDropdownOptionsForFilter: [
        ...subjectsOption,
        ...unassignedToAnySubjectOption,
      ],
      subjectDropdownOptionsForModal: subjectsOption,
    };
  }, [subjectsData]);

  const onActionClick = ({ route, obj }) => {
    setModalConfig({ show: true, screen: route });
    setStandardObj(obj);
  };

  const onClickCreateStandard = () => {
    onActionClick({
      route: "createStandard",
    });
  };

  const changeSearchTerm = value => {
    changeStandardSearchString({ standards: value });
  };

  const onCurriculumFilterDropDownItemClick = val => {
    updateFilters({
      standards: { ...standardFilters, curriculumTypeFilters: val },
    });
  };

  const onSubjectFilterDropDownItemClick = ids => {
    updateFilters({
      standards: { ...standardFilters, subjectFilters: ids },
    });
  };

  const onClickEditStandard = standard => {
    onActionClick({
      route: "editStandardInfo",
      obj: standard,
    });
  };

  const downloadStandardBenchmarkSet = async ({
    standardSetId,
    standardSetName,
  }) => {
    try {
      const { data } = await client.query({
        query: getStandardBenchmarkSetDownloadUrlQuery,
        variables: { standardSetId },
      });
      const { url } = _.get(data, "planner.standardBenchmarkSetDownload");
      if (url) {
        downloadFromUrl({
          attachment: {
            url,
            mimeType:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            name: `${standardSetName}_SnS.xlsx`,
          },
          shouldAskForSave: true,
          downloadUrl: url,
        });
      }
    } catch {
      setToastMsg("Something went wrong");
    }
  };

  const getActionMenu = standardSet => {
    const { id, title } = standardSet || {};
    const actions = [
      {
        label: "Edit",
        clickAction: () => onClickEditStandard(standardSet),
        key: "edit",
      },
      {
        label: "Download",
        clickAction: () => {
          downloadStandardBenchmarkSet({
            standardSetId: id,
            standardSetName: title,
          });
        },
        key: "download",
      },
      {
        label: "Delete",
        clickAction: () => {
          setStandardObj(standardSet);
          setShowDeleteDialogBox(true);
        },
        isDestructive: true,
        key: "delete",
      },
    ];
    return actions;
  };

  const getFeedData = () => {
    return _.map(standards, standard => {
      const subjectNames = _.map(
        _.filter(subjectsData, subject =>
          _.includes(standard.node.globalSubjects, subject.value)
        ),
        "label"
      );
      const {
        title,
        curriculumName,
        country,
        state,
        curriculumTypes,
        code,
        benchmarksCount,
        id,
      } = _.get(standard, "node", {}) || {};

      const strandsText = t("common:strand_with_count", {
        count: _.get(benchmarksCount, "Strands", 0),
      });
      const objectivesText = t("common:objective_with_count", {
        count: _.get(benchmarksCount, "Objectives", 0),
      });
      const learningOutcomesText = t("common:learning_outcome_with_count", {
        count: _.get(benchmarksCount, "Learning Outcomes", 0),
      });

      const level4Count = _.get(benchmarksCount, "Level 4", 0);
      const level5Count = _.get(benchmarksCount, "Level 5", 0);
      const level4Text = `, ${t("common:label_count", {
        label: t("common:level_with_count", { count: 4 }),
        count: level4Count,
      })}`;
      const level5Text = `, ${t("common:label_count", {
        label: t("common:level_with_count", { count: 5 }),
        count: level5Count,
      })}`;

      const count = `${strandsText}, ${objectivesText}, ${learningOutcomesText}${
        level4Count ? level4Text : ""
      }${level5Count ? level5Text : ""}`;

      return {
        id,
        code,
        standardName: title,
        curriculumName: curriculumName ? curriculumName : "-",
        country: !_.isEmpty(country) ? country : "N/A",
        state: !_.isEmpty(state) ? state : "N/A",
        curriculumTypes,
        globalSubjects: subjectNames,
        count,
        action: getActionMenu(standard.node),
      };
    });
  };

  const closeDialogueBoxDisplay = () => {
    setShowDeleteDialogBox(false);
  };

  const onDialogConfirmClick = async () => {
    setIsStateLoading(true);
    const { id } = standardObj;
    await deleteStandard({
      id,
    });

    setIsStateLoading(false);
    closeDialogueBoxDisplay();
  };

  const closeModal = () => {
    setModalConfig({ show: false, screen: null });
    setStandardObj({});
  };

  const renderCreateStandardModal = () => {
    if (modalConfig.show) {
      const { mode, title } = _.get(DIALOGBOX_CONFIG, [modalConfig.screen]);
      const standardObject = _.isEmpty(standardObj) ? {} : standardObj;

      return (
        <ManageStandard
          closeModal={closeModal}
          standardObject={standardObject}
          mode={mode}
          headerTitle={title}
          getStandardFeed={getStandardFeed}
          subjectDropdownOptions={subjectDropdownOptionsForModal}
        />
      );
    } else {
      return null;
    }
  };

  const getCurriculumTypesSelectedOptions = () => {
    return _.map(filters.curriculumTypes, option => {
      const selectedCurriculumObj = _.find(
        CURRICULUM_FLAVOURS,
        opt => opt?.value === option
      );
      return _.get(selectedCurriculumObj, "label");
    });
  };

  const getCurriculumTypesFilterLabelValue = () => {
    const labelValue = getLabelValueForMultiFilterLabelButton({
      options: CURRICULUM_FLAVOURS,
      selectedOptions: getCurriculumTypesSelectedOptions(),
      t,
    });

    return labelValue;
  };

  const getSubjectsSelectedOptions = () => {
    return _.map(standardFilters.subjectFilters, option => {
      const selectedOptionObj = _.find(
        subjectDropdownOptionsForFilter,
        opt => opt?.value === option
      );
      return _.get(selectedOptionObj, "label");
    });
  };

  const getSubjectsFilterLabelValue = () => {
    const labelValue = getLabelValueForMultiFilterLabelButton({
      options: _.filter(
        subjectDropdownOptionsForFilter,
        option => !option.isDivider
      ),
      selectedOptions: getSubjectsSelectedOptions(),
      t,
    });

    return labelValue;
  };

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.innerContainer}>
        <div className={classes.headerContainer}>
          <Header
            onClickCreateStandard={onClickCreateStandard}
            goToBack={goToBack}
          />
        </div>

        <div className={classes.header}>
          <div className={classes.leftHeader}>
            <SearchInput
              placeholder={"Search.."}
              onChange={changeSearchTerm}
              value={filters.searchText}
            />
          </div>

          <div className={classes.rightHeader}>
            <div className={classes.selectCurriculumContainer}>
              <ChecklistDropdown
                options={CURRICULUM_FLAVOURS}
                onChange={onCurriculumFilterDropDownItemClick}
                value={filters.curriculumTypes}
                showAllOption={true}
              >
                <FilterLabelButton
                  label={"Curriculum flavor"}
                  size={"medium"}
                  variant={"plain"}
                  labelValue={getCurriculumTypesFilterLabelValue()}
                />
              </ChecklistDropdown>
            </div>

            <div className={classes.selectSubjectContainer}>
              <ChecklistDropdown
                options={subjectDropdownOptionsForFilter}
                onChange={onSubjectFilterDropDownItemClick}
                isSearchEnabled={true}
                value={standardFilters.subjectFilters}
                showAllOption={true}
              >
                <FilterLabelButton
                  label={"Subjects"}
                  size={"medium"}
                  variant={"plain"}
                  labelValue={getSubjectsFilterLabelValue()}
                />
              </ChecklistDropdown>
            </div>
          </div>
        </div>

        <div className={classes.tableContainer}>
          <FeedListv2
            columnList={columnList()}
            feedData={getFeedData()}
            isData={isData}
            isLoading={isLoading}
          />
        </div>

        {showDeleteDialogBox && (
          <DialogueBox
            modalTitle={DIALOGS_INFO.DELETE_STANDARD.title}
            onClickButton2={onDialogConfirmClick}
            modalBody={DIALOGS_INFO.DELETE_STANDARD.message({
              title: standardObj.title,
            })}
            toggleDialogueBoxDisplay={closeDialogueBoxDisplay}
            button1={DIALOGS_INFO.DELETE_STANDARD.cancelButtonText}
            button2={DIALOGS_INFO.DELETE_STANDARD.confirmButtonText}
          />
        )}

        {networkStatus == 3 && (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        )}
        {isStateLoading && <FullScreenLoader />}

        {renderCreateStandardModal()}
      </div>
    </div>
  );
};

const mapActionCreators = {
  deleteStandard,
  changeStandardSearchString,
  goToBack,
  updateFilters,
  setToastMsg,
  downloadFromUrl,
};

const mapStateToProps = state => {
  const standardFilters = state.tooling.filters.standards;
  const { subjectFilters, curriculumTypeFilters } = standardFilters;
  const subjects = _.filter(subjectFilters, ids => ids !== "UNASSIGNED");
  const unAssignedToSubject = _.size(subjects) !== _.size(subjectFilters);

  const filters = {
    searchText: state.tooling.searchString.standards,
    subjects,
    curriculumTypes: curriculumTypeFilters,
    ...(unAssignedToSubject ? { unAssignedToSubject } : {}),
  };

  return {
    filters,
    standardFilters,
    isData: true,
    isLoading: false,
  };
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getStandardFeedQuery, {
    name: "getStandardFeed",
    options: ({ filters }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: { filters },
      };
    },
    props({ getStandardFeed, ownProps: { isData, isLoading, filters } }) {
      const standardDetails = _.get(
        getStandardFeed,
        "planner.standardBenchmarkSetFeed",
        {}
      );

      const standards = _.get(standardDetails, "edges", []);
      const totalCount = _.get(standardDetails, "totalCount", 0);

      return {
        totalCount,
        standards,
        getStandardFeed,
        hasNextPage: _.get(standardDetails, "pageInfo.hasNextPage", false),
        networkStatus: getStandardFeed["networkStatus"],
        isData: !_.isEmpty(standardDetails) && isData,
        isLoading:
          _.includes([1, 2], getStandardFeed["networkStatus"]) || isLoading,
        loadMoreFeed: () => {
          getStandardFeed.fetchMore({
            variables: {
              ...filters,
              after: _.get(standardDetails, "pageInfo.endCursor", undefined),
            },
          });
        },
      };
    },
  }),
  graphql(getAllGlobalSubjectsQuery, {
    name: "getAllGlobalSubjectsQuery",
    props: ({ getAllGlobalSubjectsQuery, ownProps: { isData, isLoading } }) => {
      const allSubjects = _.get(
        getAllGlobalSubjectsQuery,
        "platform.allGlobalSubjects",
        []
      );
      return {
        subjectsData: getSubjectsData({ allSubjects }),
        isData: _.has(getAllGlobalSubjectsQuery, "platform") && isData,
        isLoading:
          _.includes([1, 2], getAllGlobalSubjectsQuery["networkStatus"]) ||
          isLoading,
      };
    },
  })
)(StandardV2);
